import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CircleHelp, LogOut, UserRound } from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { useLocale } from "@/context/LocaleContext";
import { ThemeSwitch } from "@/components/ThemeSwitch";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Account menu: theme, language, help, logout — shared across app headers.
 * Tournament/project actions stay in separate project menus.
 */
export function AccountMenu({
  user,
  helpReturnTo,
  onLogout,
  className = "",
  testId = "account-menu",
}) {
  const { t } = useLocale();
  const { refreshUser } = useAuth();
  const [dailyEmailBusy, setDailyEmailBusy] = useState(false);
  const dailyEmailOn = user?.daily_email !== false;

  const onDailyEmailChange = async (checked) => {
    setDailyEmailBusy(true);
    try {
      await axios.patch(`${API}/auth/preferences`, { daily_email: checked });
      await refreshUser();
    } catch {
      toast.error(t("account.dailyEmailErr"));
    } finally {
      setDailyEmailBusy(false);
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`btn-ghost inline-flex items-center gap-2 shrink-0 px-2.5 sm:px-3 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-inset data-[state=open]:border-[var(--line-2)] data-[state=open]:text-[var(--text)] ${className}`}
          aria-label={t("account.menuAria")}
        >
          <UserRound size={18} className="shrink-0" aria-hidden />
          {user?.name ? (
            <span className="hidden sm:inline text-sm truncate max-w-[9rem] md:max-w-[12rem]">{user.name}</span>
          ) : (
            <span className="hidden sm:inline text-sm">{t("account.menu")}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 bg-[var(--panel)] border-[var(--line-2)] text-[var(--text)] p-2"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-[var(--text-dim)] font-semibold px-1">
          {t("account.settingsTitle")}
        </DropdownMenuLabel>
        {user?.name && (
          <div className="px-1 pb-2">
            <div className="text-sm font-medium truncate" data-testid="user-name">{user.name}</div>
            {user.email && <div className="text-xs text-[var(--text-dim)] truncate">{user.email}</div>}
          </div>
        )}
        <div
          className="px-1 py-2 space-y-3"
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[var(--text-dim)]">{t("theme.ariaLabel")}</span>
            <ThemeSwitch matchedWidth />
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[var(--text-dim)]">{t("account.language")}</span>
            <LanguageSwitch matchedWidth />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-[var(--text-dim)]">{t("account.dailyEmail")}</div>
              <div className="text-[10px] leading-snug text-[var(--text-dim)] opacity-80">{t("account.dailyEmailHint")}</div>
            </div>
            <Switch
              checked={dailyEmailOn}
              disabled={dailyEmailBusy}
              onCheckedChange={onDailyEmailChange}
              aria-label={t("account.dailyEmail")}
              data-testid="account-daily-email-switch"
            />
          </div>
        </div>
        <DropdownMenuSeparator className="bg-[var(--line)]" />
        {helpReturnTo && (
          <DropdownMenuItem asChild className="cursor-pointer focus:bg-[var(--dropdown-hover-bg)] focus:text-[var(--dropdown-hover-fg)]">
            <Link to="/help" state={{ returnTo: helpReturnTo }} data-testid="account-help-link" className="flex items-center gap-2">
              <CircleHelp size={16} aria-hidden />
              {t("help.nav")}
            </Link>
          </DropdownMenuItem>
        )}
        {onLogout && (
          <DropdownMenuItem
            data-testid="btn-logout"
            className="cursor-pointer focus:bg-[var(--dropdown-hover-bg)] focus:text-[var(--dropdown-hover-fg)]"
            onClick={onLogout}
          >
            <LogOut size={16} aria-hidden />
            {t("dashboard.logout")}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
